import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Avatar } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CaretUp } from '@phosphor-icons/react';

import useTranslation from 'customHooks/translate';
import { ACCOUNT_INFO } from 'events/types';
import { useAuth } from 'providers/user-context';
import { getAccountData } from 'services/accounts';
import { truncateText } from 'utils';
import ContactUsForm from 'pages/Dialogs/ContactUsForm';

import './index.css';

interface IProps {
  isSidebarOpen: boolean;
}

const SidebarProfile = (props: IProps) => {
  const { isSidebarOpen } = props;

  const translation = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  const handleClick = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsMenuOpen(false);
  };

  const accountInfoQuery = useQuery({
    queryKey: [ACCOUNT_INFO],
    queryFn: getAccountData,
    select: (response) => response.data.data,
    refetchOnWindowFocus: true,
    staleTime: 0,
    cacheTime: 0,
  });

  const accountData = accountInfoQuery.data;

  useEffect(() => {
    if (!accountData) {
      accountInfoQuery.refetch();
    }
  }, [accountData]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div ref={containerRef} className="sidebar-profile-menu-container">
      {isSidebarOpen ? (
        <div
          onClick={handleClick}
          className={`flex cursor-pointer items-center rounded-[8px] border border-[#F5F7F9] p-[12px] ${
            isSidebarOpen ? 'justify-between' : 'justify-center'
          }`}
        >
          <div className="flex items-center gap-[12px]">
            <Avatar alt={accountData?.fullName} src="assets/placeholder.svg" />
            <p className="text-[14px] font-medium text-[#1D1D1D]">
              {truncateText(accountData?.fullName, 10)}
            </p>
          </div>
          <div className="flex h-[30px] w-[30px] items-center justify-center rounded-[50%] bg-[#F5F7F9] text-[#292D32]">
            <CaretUp weight="bold" />
          </div>
        </div>
      ) : (
        <div
          onClick={handleClick}
          className="flex cursor-pointer items-center justify-center"
        >
          <Avatar alt={accountData?.fullName} src="assets/placeholder.svg" />
        </div>
      )}
      {isMenuOpen ? (
        <div className="sidebar-profile-custom-menu">
          <button
            type="button"
            className="sidebar-profile-menu-item"
            onClick={() => {
              navigate('/profile', { replace: true });
              handleClose();
            }}
          >
            <UserOutlined />
            <span>{translation.ProfileButton.Profile}</span>
          </button>
          <button
            type="button"
            className="sidebar-profile-menu-item"
            onClick={() => {
              navigate('/settings', { replace: true });
              handleClose();
            }}
          >
            <SettingOutlined />
            <span>{translation.ProfileButton.Settings}</span>
          </button>
          <button
            type="button"
            className="sidebar-profile-menu-item"
            onClick={() => {
              setIsContactFormOpen(true);
              setIsMenuOpen(false);
            }}
          >
            <InfoCircleOutlined />
            <span>{translation.checkEmail.ContactLink}</span>
          </button>
          <div className="sidebar-profile-menu-divider" />
          <button
            type="button"
            className="sidebar-profile-menu-item sidebar-profile-menu-item-logout"
            onClick={() => {
              handleClose();
              logout();
            }}
          >
            <LogoutOutlined />
            <span>{translation.ProfileButton.Logout}</span>
          </button>
        </div>
      ) : null}
      <ContactUsForm
        open={isContactFormOpen}
        handleClose={() => setIsContactFormOpen(false)}
        currentAccountData={accountData}
      />
    </div>
  );
};

export default SidebarProfile;
